import { Link } from 'react-router-dom';

import type { ArticleSummary } from '@/types/newsService';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/lib/format';

interface ArticleCardProps {
  article: ArticleSummary;
  locale?: string;
  className?: string;
}

function pickContent(article: ArticleSummary, locale = 'en') {
  const exact = article.content.find((c) => c.locale === locale);
  return exact ?? article.content[0];
}

export function ArticleCard({ article, locale = 'en', className }: ArticleCardProps) {
  const content = pickContent(article, locale);
  if (!content) return null;

  return (
    <Link
      to={`/news/${article.slug}`}
      className={cn(
        'flex flex-col gap-2 overflow-hidden rounded-xl bg-bg-secondary',
        'transition-opacity active:opacity-80',
        className,
      )}
    >
      {article.heroImage?.url && (
        <div className="relative aspect-[16/9] overflow-hidden bg-bg-tertiary">
          <img
            src={article.heroImage.thumbnailUrl ?? article.heroImage.url}
            alt={article.heroImage.alt}
            className="h-full w-full object-cover"
            loading="lazy"
          />
          {article.primaryTag && (
            <span className="absolute left-2 top-2 rounded-full bg-bg/80 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-brand backdrop-blur">
              {article.primaryTag.name}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col gap-1 p-3 pt-1">
        <h3 className="line-clamp-2 text-base font-semibold leading-snug text-fg">
          {content.title}
        </h3>
        {content.subtitle && (
          <p className="line-clamp-2 text-sm text-fg-muted">{content.subtitle}</p>
        )}
        <div className="mt-1 flex items-center gap-2 text-[11px] text-fg-muted">
          <span>{article.authorName}</span>
          <span aria-hidden>·</span>
          <span>{formatRelative(article.publishedAt)}</span>
          <span aria-hidden>·</span>
          <span>{article.readingTimeMinutes} min</span>
        </div>
      </div>
    </Link>
  );
}
